import React, { useEffect, useState } from 'react';
import { setPageTitle } from './utils/accessibility';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { AdvancedAssessmentForm } from './components/forms/AdvancedAssessmentForm';
import { AuditResultsDashboard } from './components/AuditResultsDashboard';
import { Footer } from './components/Footer';
import { WelcomePage } from './components/auth/WelcomePage';
import { UserDashboard } from './components/dashboard/UserDashboard';
import { AboutPage } from './components/pages/AboutPage';
import { SecurityPage } from './components/pages/SecurityPage';
import { SecurityNotice } from './components/security/SecurityNotice';
import { Card } from './components/ui/Card';
import { useAssessment } from './hooks/useAssessment';
import { useFormState } from './hooks/useFormState';
import { useAuth } from './hooks/useAuth';
import type { AdvancedRedTeamConfig } from './utils/advancedRedTeamLogic';

function App() {
  const { user, loading: authLoading, error: authError, signIn, signUp, signOut, checkAuthState } = useAuth();
  const [currentView, setCurrentView] = useState<'assessment' | 'dashboard' | 'about' | 'security'>('assessment');

  const {
    selectedPersona,
    setSelectedPersona,
    targetUrl,
    setTargetUrl,
    basePrompt,
    setBasePrompt,
    targetModel,
    setTargetModel
  } = useFormState();

  const {
    modelResponse,
    assessment,
    loading,
    error,
    runAssessment
  } = useAssessment();

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  useEffect(() => {
    const titles = {
      assessment: 'AI Security Assessment',
      dashboard: 'Security Dashboard',
      about: 'About Red Set',
      security: 'Security & Compliance'
    };
    setPageTitle(titles[currentView]);
  }, [currentView]);
  if (!user && !authLoading) {
    return (
      <WelcomePage
        onSignIn={signIn}
        onSignUp={signUp}
        loading={authLoading}
        error={authError}
      />
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const handleDeploy = (config?: AdvancedRedTeamConfig) => {
    runAssessment(basePrompt, selectedPersona, targetUrl, targetModel, config);
  };

  // Handle navigation for unauthenticated users
  if (!user && !authLoading) {
    if (currentView === 'about') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
          <Header onNavigate={setCurrentView} currentView={currentView} />
          <AboutPage />
          <Footer />
        </div>
      );
    }
    
    if (currentView === 'security') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
          <Header onNavigate={setCurrentView} currentView={currentView} />
          <SecurityPage />
          <Footer />
        </div>
      );
    }
    
    return (
      <WelcomePage
        onSignIn={signIn}
        onSignUp={signUp}
        loading={authLoading}
        error={authError}
      />
    );
  }

  if (currentView === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <Header user={user} onSignOut={signOut} onNavigate={setCurrentView} currentView={currentView} />
        <UserDashboard />
        <Footer />
      </div>
    );
  }

  if (currentView === 'about') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <Header user={user} onSignOut={signOut} onNavigate={setCurrentView} currentView={currentView} />
        <AboutPage />
        <Footer />
      </div>
    );
  }

  if (currentView === 'security') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <Header user={user} onSignOut={signOut} onNavigate={setCurrentView} currentView={currentView} />
        <SecurityPage />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
      <Header user={user} onSignOut={signOut} onNavigate={setCurrentView} currentView={currentView} />
      <Hero />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SecurityNotice />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card variant="red">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-1 h-6 bg-red-500 mr-3"></div>
              Adversarial Audit Control
            </h2>
            
            <AdvancedAssessmentForm
              selectedPersona={selectedPersona}
              onPersonaChange={setSelectedPersona}
              targetUrl={targetUrl}
              onTargetUrlChange={setTargetUrl}
              basePrompt={basePrompt}
              onBasePromptChange={setBasePrompt}
              targetModel={targetModel}
              onTargetModelChange={setTargetModel}
              onDeploy={handleDeploy}
              loading={loading}
              error={error}
            />
            
            <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-500/30 rounded-lg p-4">
              <h4 className="font-medium text-red-900 mb-2 flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                Autonomous Audit Protocol
              </h4>
              <ul className="text-sm text-red-800 space-y-1">
                <li>• Deploy autonomous red team agents with predefined attack vectors</li>
                <li>• Execute targeted probes against specified AI endpoints</li>
                <li>• Analyze model responses for security vulnerabilities</li>
                <li>• Generate comprehensive vulnerability assessments</li>
              </ul>
            </div>
          </Card>

          <div>
            <AuditResultsDashboard 
              modelResponse={modelResponse} 
              assessment={assessment} 
              loading={loading} 
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default App;